import * as i0 from '@angular/core';
import { Injectable, Inject, isDevMode, Optional, NgModule } from '@angular/core';
import { isString, isObject, has } from 'lodash-es';
import { of, Subject } from 'rxjs';
import * as i1 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

/**
 * Send error to the Logasaurus endpoint.
 */
class LogasaurusService {
    constructor(url, maxStringLength, _http) {
        this.url = url;
        this.maxStringLength = maxStringLength;
        this._http = _http;
    }
    /**
     * Send error to Logasaurus service endpoint.
     * @param {array} args the arguments.
     */
    send(...args) {
        const logMessage = this.truncateMessageObject(args);
        if (this.url) {
            return this._http.post(this.url, logMessage);
        }
        return of(`LOGASAURUS_URL is not provided, skipping the post of the error to the server`);
    }
    truncateMessageObject(logMessage, depth = 4) {
        for (const prop in logMessage) {
            if (isString(logMessage[prop]) &&
                logMessage[prop].length > this.maxStringLength) {
                // protect against assignment errors on
                // non-writable properties in strict mode
                try {
                    logMessage[prop] = this.truncateMessageString(logMessage[prop]);
                }
                catch (error) {
                    // fail silently
                }
            }
            else if (isObject(logMessage[prop]) && depth) {
                depth = depth - 1;
                // condition is also true for arrays
                this.truncateMessageObject(logMessage[prop], depth);
            }
        }
        return logMessage;
    }
    truncateMessageString(str) {
        return str.substr(0, this.maxStringLength) + '...[truncated]';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LogasaurusService, deps: [{ token: 'LOGASAURUS_URL' }, { token: 'MAX_STRING_LENGTH' }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LogasaurusService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LogasaurusService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['LOGASAURUS_URL']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['MAX_STRING_LENGTH']
                }] }, { type: i1.HttpClient }] });

/**
 * Interface for Logger Options.
 */
class LoggerOptions {
}

/**
 * Class for a Console that doesn't perform any logging.
 */
class NoConsole {
    countReset(label) { }
    timeLog(label, ...data) { }
    assert(test, message, ...optionalParams) { }
    clear() { }
    count(countTitle) { }
    debug(message, ...optionalParams) { }
    dir(value, ...optionalParams) { }
    dirxml(value) { }
    error(message, ...optionalParams) { }
    exception(message, ...optionalParams) { }
    group(groupTitle, ...optionalParams) { }
    groupCollapsed(groupTitle, ...optionalParams) { }
    groupEnd() { }
    info(message, ...optionalParams) { }
    log(message, ...optionalParams) { }
    msIsIndependentlyComposed(element) {
        return true;
    }
    profile(reportName) { }
    profileEnd() { }
    select(element) { }
    table(...data) { }
    time(timerName) { }
    timeEnd(timerName) { }
    trace(message, ...optionalParams) { }
    warn(message, ...optionalParams) { }
    memory(message, ...optionalParams) { }
    markTimeline(message, ...optionalParams) { }
    timeStamp(message, ...optionalParams) { }
    timeline(message, ...optionalParams) { }
    timelineEnd(message, ...optionalParams) { }
}

/**
 * Return the global native browser window object
 */
function _window() {
    if (globalThis.window) {
        return window;
    }
}
/**
 * Export a reference of the global native browser window object.
 */
class WindowRef {
    getNativeWindow() {
        return _window();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WindowRef, decorators: [{
            type: Injectable
        }] });

/**
 * Service to perform logging and send error to
 * logasaurus backend, see {@link LogasaurusService},
 * or emit the error message via an observable
 * in embedded (RA Hybrid) scenarios.
 */
class LoggerService {
    constructor(logasaurusService, windowRef, url, options) {
        this.logasaurusService = logasaurusService;
        this.windowRef = windowRef;
        this.url = url;
        this.options = options;
        this.enabled = true;
        this.debugEnabled = isDevMode();
        this.console = console;
        if (options) {
            this.enabled = options.enabled;
            this.debugEnabled = options.debug;
            this.console = options.consoleObject ? options.consoleObject : console;
        }
        if (!this.enabled) {
            this.console = new NoConsole();
        }
        // If 'embeddable' option value is not passed (or passed as false) -> Not embedded
        // If WDPR RA JavaScript Native Bridge is not installed -> Not embedded
        this.isEmbedded = options && options.embeddable
            && has(this.windowRef.getNativeWindow(), 'disney.device.embedded')
            && this.windowRef.getNativeWindow().disney.device.embedded;
        this.errorEvents$ = new Subject();
    }
    /**
     * log a message.
     * @param {string} message the arguments.
     * @param {array} args the arguments.
     */
    log(message, ...args) {
        const messages = this.parseMessage(args);
        this.console.log(message, ...messages);
    }
    /**
     * Logs a warn a message.
     * @param {string} message the arguments.
     * @param {array} args the arguments.
     */
    warn(message, ...args) {
        const messages = this.parseMessage(args);
        this.console.warn(message, ...messages);
    }
    /**
     * log an error and send the error to
     * logasaurus backend, see {@link LogasaurusService}.
     * @param {string} message the arguments.
     * @param {array} args the arguments.
     */
    error(message, ...args) {
        // Send data to EUM AppDynamics Monitoring to log this custom error
        // docs: https://github.disney.com/WDPR-RA-UI/docs/blob/master/docs/appdeum/README.md
        if (this.windowRef.getNativeWindow() && this.windowRef.getNativeWindow().ADRUM) {
            if (args && args.length > 0) {
                this.adrumPost(message);
                for (const arg of args) {
                    this.adrumPost(arg);
                }
            }
            else {
                console.error('Logger Error: No Log message available to store in ADRUM');
            }
        }
        // We only need the error parsed for the browser's console.
        const messages = this.parseMessage(args);
        this.console.error(message, ...messages);
        // Send error via logasaurus only if the Angular SPA is NOT running in a mobile-embedded scenario
        // Read more here: https://github.disney.com/wdpr-ra-mobile/rahybrid-docs/blob/master/docs/index.md#ra-hybrid-embedded
        if (this.isEmbedded) {
            this.emitErrorMessage(message, ...args);
        }
        else {
            this.send(message, ...args);
        }
    }
    /**
     * log a debug message.
     * @param {string} message the arguments.
     * @param {array} args the arguments.
     */
    debug(message, ...args) {
        if (this.debugEnabled) {
            const messages = this.parseMessage(args);
            this.console.debug(message, ...messages);
        }
    }
    /**
     * Send log information to logasaurus backend, see {@link LogasaurusService}.
     * @param {string} message the message to send
     * @param {array} args the arguments.
     */
    send(message, ...args) {
        // Errors get sent to the logasaurus service
        if (this.logasaurusService && this.enabled) {
            this.logasaurusService.send([message, ...args])
                .subscribe({
                next: (_data) => {
                    if (this.debug) {
                        console.log(`Posted to ${this.url} error: ${args}`);
                    }
                },
                error: (err) => {
                    console.error(`Backend error in logasaurus service when trying to log remotely.
                      Error code ${err.status}`);
                    console.error(`${err.error}`);
                }
            });
        }
    }
    /**
     * Emit error message for embedded scenarios
     * @param {string} message the message to send
     * @param {array} args the arguments.
     */
    emitErrorMessage(message, ...args) {
        if (this.enabled) {
            this.errorEvents$.next([message, ...args]);
        }
        else {
            this.errorEvents$.error('some error');
        }
    }
    /**
     * Send data to EUM AppDynamics Monitoring.
     * docs: https://github.disney.com/WDPR-RA-UI/docs/blob/master/docs/appdeum/README.md
     * @param {any} msg the error message.
     */
    adrumPost(msg) {
        try {
            const reportAdrum = this.windowRef.getNativeWindow().ADRUM.events.Error(), message = (msg && msg.message) ? msg.message : 'No Data', stack = (msg && msg.stack) ? msg.stack : '', line = (msg && msg.line) ? msg.line : -1;
            reportAdrum.msg(message);
            reportAdrum.line(line);
            reportAdrum.stack(stack);
            this.windowRef.getNativeWindow().ADRUM.report(reportAdrum);
        }
        catch (err) {
            console.error('ADRUM Error: ', err);
        }
    }
    /**
     * Parses the error object to display it properly on the console.
     * @param {array} args the arguments to parse.
     */
    parseMessage(args) {
        let messages;
        // Iterate over array
        messages = args.map(elem => {
            if (typeof elem !== 'string') {
                return elem;
            }
            let str;
            try {
                // Try to parse JSON and access message prop
                // Fallback to whole JSON object
                const data = JSON.parse(elem);
                str = data.message || elem;
            }
            catch (e) {
                // Plain string - Eat the error
                str = elem;
            }
            return str;
        });
        return messages;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerService, deps: [{ token: LogasaurusService }, { token: WindowRef }, { token: 'LOGASAURUS_URL' }, { token: 'LOGGER_OPTIONS', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: LogasaurusService }, { type: WindowRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['LOGASAURUS_URL']
                }] }, { type: LoggerOptions, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['LOGGER_OPTIONS']
                }] }] });

/**
 * @module
 * @description
 * The LoggerModule provides services to perform logging.
 * To get started, see the {@link LoggerService} class.
 */
/**
 * LoggerModule module that includes the logger's providers.
 */
class LoggerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: LoggerModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerModule, providers: [
            WindowRef,
            {
                provide: 'LOGASAURUS_URL',
                useValue: '/error'
            },
            {
                provide: 'MAX_STRING_LENGTH',
                useValue: 10000
            },
            LoggerService,
            LogasaurusService,
            provideHttpClient(withInterceptorsFromDi())
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LoggerModule, decorators: [{
            type: NgModule,
            args: [{ declarations: [],
                    exports: [], imports: [], providers: [
                        WindowRef,
                        {
                            provide: 'LOGASAURUS_URL',
                            useValue: '/error'
                        },
                        {
                            provide: 'MAX_STRING_LENGTH',
                            useValue: 10000
                        },
                        LoggerService,
                        LogasaurusService,
                        provideHttpClient(withInterceptorsFromDi())
                    ] }]
        }] });

/*
 * Public API Surface of ra-angular-logger
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LogasaurusService, LoggerModule, LoggerOptions, LoggerService, NoConsole, WindowRef };
